import React, { useMemo } from 'react';
import {
  BrowserRouter as NativeRouter,
  Route,
  withRouter,
  Redirect,
} from 'react-router-dom';

import values from 'lodash/values';

import Login from '@/pages/Login';
import Checkout from '@/pages/Checkout';
import Home from '@/pages/Home';
import Account from '@/pages/Account';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Affiliate from '@/pages/Affiliate';
import SelectPlan from '@/pages/SelectPlan';
import CheckoutComplete from '@/pages/CheckoutComplete';
import Bancontact from '@/pages/Bancontact';
import NoSub from '@/pages/NoSub';
import Admin from '@/pages/Admin';
import AdminCollection from '@/pages/Admin/components/Collection';
import AdminInformationItem from '@/pages/Admin/components/InformationItem';
import AddCard from '@/pages/AddCard';

import LoadingOverlay from '@/components/LoadingOverlay';
import RouterError from '@/components/RouterError';

import ROUTES, { PUBLIC_ROUTES } from '@/routes';

import AuthService from '@/services/auth.service';

import { pathInRoutes, pathMatchesRoute } from '@/core/router-helpers';
import AuthContext from '@/core/AuthContext';
import { useQuery } from 'react-query';

const Routes = withRouter(function Routes({ location }) {
  const { pathname } = location;

  const {
    isLoading,
    error,
    data: user,
    refetch,
  } = useQuery('AuthService.refreshUser', async () =>
    AuthService.refreshUser()
  );

  const hasActiveSubscription = useMemo(() => {
    return (
      user?.subscription?.active &&
      Object.keys(user?.paymentMethod || {}).length > 0
    );
  }, [user]);

  if (error) {
    console.log(error);
    return <RouterError />;
  }

  if (isLoading) {
    return (
      <LoadingOverlay visible={isLoading} message="Checking Credentials" />
    );
  }

  if (!pathInRoutes(pathname, values(ROUTES))) {
    return <Redirect to={ROUTES.ROOT} />;
  }

  if (pathname === ROUTES.LOGOUT) {
    AuthService.logout().then(() => {
      window.location.href = '/';
    });

    return null;
  }

  if (pathname === ROUTES.ROOT && !user) {
    return <Redirect to={ROUTES.LOGIN} />;
  }

  if (pathname === ROUTES.ACCOUNT && !hasActiveSubscription) {
    return <Redirect to={ROUTES.NO_SUB} />;
  }
  if (pathname === ROUTES.NO_SUB && hasActiveSubscription) {
    return <Redirect to={ROUTES.ACCOUNT} />;
  }

  if (pathname === ROUTES.ROOT && user) {
    if (hasActiveSubscription) {
      return <Redirect to={ROUTES.ACCOUNT} />;
    }

    return <Redirect to={ROUTES.NO_SUB} />;
  }

  if (!user && !pathInRoutes(pathname, PUBLIC_ROUTES)) {
    return <Redirect to={ROUTES.ROOT} />;
  }

  if (user && pathMatchesRoute(pathname, ROUTES.LOGIN)) {
    return <Redirect to={ROUTES.ROOT} />;
  }

  return (
    <AuthContext.Provider value={{ user, refetch }}>
      <Route exact path={ROUTES.HOME} component={Home} />
      <Route exact path={ROUTES.CHECKOUT} component={Checkout} />
      <Route exact path={ROUTES.LOGIN} component={Login} />
      <Route exact path={ROUTES.NO_SUB} component={NoSub} />
      <Route exact path={ROUTES.ADMIN} component={Admin} />
      <Route exact path={ROUTES.ACCOUNT} component={Account} />
      <Route
        exact
        path={ROUTES.CREATE_ACCOUNT}
        component={() => (
          <Redirect
            to={{ pathname: ROUTES.SELECT_PLAN, search: location.search }}
          />
        )}
      />
      <Route exact path={ROUTES.FORGOT_PASSWORD} component={ForgotPassword} />
      <Route exact path={ROUTES.RESET_PASSWORD} component={ResetPassword} />
      <Route exact path={ROUTES.AFFILIATE} component={Affiliate} />
      <Route exact path={ROUTES.SELECT_PLAN} component={SelectPlan} />
      <Route
        exact
        path={ROUTES.CHECKOUT_COMPLETE}
        component={CheckoutComplete}
      />
      <Route exact path={ROUTES.ADMIN_COLLECTION} component={AdminCollection} />
      <Route
        exact
        path={ROUTES.ADMIN_INFORMATION_ITEM}
        component={AdminInformationItem}
      />
      <Route exact path={ROUTES.BANCONTACT} component={Bancontact} />
      <Route exact path={ROUTES.ADD_CARD} component={AddCard} />
    </AuthContext.Provider>
  );
});

export default function Router() {
  return (
    <NativeRouter>
      <Routes />
    </NativeRouter>
  );
}
